import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { get } from "../../services/api";
import { toast } from "react-toastify";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";
import { customTableStyles } from "../../assets/CustomTableStyles";
import Select from "react-select";

function ActivityCertificate() {
  const token = localStorage.getItem("token");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");
  const roleId = localStorage.getItem("roleId");
  const userId = localStorage.getItem("userId");

  const PERMISSIONS = {
    CAMPUS_OFFICER: {
      roleIds: ["3"],
      actions: [
        {
          name: "generateCertificate",
          icon: "fas fa-certificate",
          handler: "handleGenerateCertificate",
          tooltip: "Generate Certificate",
          visible: (row) => !row.isCertificateGenerated,
          disabled: () => false,
        },
        {
          name: "printCertificate",
          icon: "fas fa-print",
          handler: "handlePrintCertificate",
          tooltip: "Print Certificate",
          visible: (row) => row.isCertificateGenerated,
          disabled: () => false,
        },
      ],
    },
    ACTIVITY_SUPERVISOR: {
      roleIds: ["13"],
      actions: [
        {
          name: "generateCertificate",
          icon: "fas fa-certificate",
          handler: "handleGenerateCertificate",
          tooltip: "Generate Certificate",
          visible: (row) => !row.isCertificateGenerated,
          disabled: () => false,
        },
        {
          name: "printCertificate",
          icon: "fas fa-print",
          handler: "handlePrintCertificate",
          tooltip: "Print Certificate",
          visible: (row) => row.isCertificateGenerated,
          disabled: () => false,
        },
      ],
    },
    PARENT: {
      roleIds: ["9", "10"],
      actions: [
        {
          name: "printCertificate",
          icon: "fas fa-print",
          handler: "handlePrintCertificate",
          tooltip: "Print Certificate",
          visible: (row) => row.isCertificateGenerated,
          disabled: () => false,
        },
      ],
    },
  };

  const getActionsForRole = (roleId) => {
    const roleConfig = Object.values(PERMISSIONS).find((role) =>
      role.roleIds?.includes(String(roleId))
    );
    return roleConfig ? roleConfig.actions : [];
  };

  const {
    formState: { errors, isSubmitting },
    control,
    watch,
  } = useForm({
    defaultValues: {
      // selectedDate: new Date().toISOString().split("T")[0],
      selectedDate: "",
      selectedActivity: null,
    },
  });

  const [loading, setLoading] = useState(false);
  const [centerId, setCenterId] = useState(savedCenterId || null);

  const [activityAttendanceData, setActivityAttendanceData] = useState([]);
  const [activities, setActivities] = useState([]);

  const selectedDate = watch("selectedDate");
  const selectedActivity = watch("selectedActivity");

  const getActivities = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `CampusActivity/GetAllActivites?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        const activityOptions = response.activities.map((activity) => ({
          value: activity.sr, // Use sr as the unique key
          label: activity.activityName,
          actvityCategoryId: activity.actvityCategoryId,
          id: activity.id, // Include id if needed for getActivityAttendanceList
        }));
        setActivities(activityOptions);
      }
    } catch (error) {
      console.error("Error fetching activities:", error);
      toast.error("Failed to load activities.");
    }
  };

  const getActivityAttendanceList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const userIdToSend = ["3", "13"].includes(roleId) ? 0 : userId;
    const activityId = selectedActivity?.id || 0;
    const actvityCategoryId = selectedActivity?.actvityCategoryId || 0;
    const dateToSend = selectedDate || "";

    try {
      const response = await get(
        `CampusActivity/GetActivityAttendanceList?centerId=${centerId}&ActivityDate=${dateToSend}&userId=${userIdToSend}&activityId=${activityId}&actvityCategoryId=${actvityCategoryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const cleanedData = response?.activityAttendanceDtos?.map(
        (row, index) => ({
          ...row,
          rating: row.rating ?? 0,
          remarks: row.remarks ?? "",
          isCertificateGenerated: row.isCertificateGenerated ?? false,
          rowKey: `${row.campusActivityApplicationId}-${row.activityId}-${row.scheduleDateTime}-${index}`, // Enhanced unique rowKey
        })
      );

      setActivityAttendanceData(cleanedData || []);
    } catch (error) {
      console.error("Error fetching attendance list:", error);
      setActivityAttendanceData([]);
    }
  };

  useEffect(() => {
    getActivities();
  }, []);

  useEffect(() => {
    getActivityAttendanceList();
  }, [selectedDate, selectedActivity, centerId]);

  const handleStarClick = (rowId, newRating) => {
    const updatedData = activityAttendanceData.map((row) => {
      if (row.campusActivityApplicationId === rowId) {
        return { ...row, rating: newRating };
      }
      return row;
    });
    setActivityAttendanceData(updatedData);
  };

  const handleGenerateCertificate = async (studentId, activityId) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }
    setLoading(true);
    try {
      const response = await get(
        `CampusActivity/GenerateCerticateChallan?id=${studentId}&activityId=${activityId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response?.isSuccess) {
        toast.success("Certificate generated successfully!");
        getActivityAttendanceList();
      }
    } catch (error) {
      console.error("Error generating certificate:", error);
      toast.error("Failed to generate certificate.");
    } finally {
      setLoading(false);
    }
  };

  const handlePrintCertificate = async (studentId) => {
    if (!studentId) {
      toast.error("Error: Student ID is missing.");
      return;
    }

    setLoading(true);

    const API_BASE_URL = process.env.REACT_APP_API_URL;

    try {
      const response = await fetch(
        `${API_BASE_URL}CampusActivity/GetActivityCertificate?id=${studentId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      window.open(url);

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Error opening the certificate:", error);
      toast.error("Failed to print certificate.");
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "studentName",
        header: "Pupil",
        // size: 150,
      },
      {
        accessorKey: "activityName",
        header: "Activity",
        size: 200,
      },
      {
        accessorKey: "rating",
        header: "Rating",
        // size: 150,
        Cell: ({ row }) => (
          <div>
            {Array.from({ length: 5 }, (_, index) => (
              <span
                key={index}
                className={`fa fa-star ${index < row.original.rating ? "checked" : ""}`}
                onClick={() =>
                  handleStarClick(row.original.campusActivityApplicationId, index + 1)
                }
                style={{
                  cursor: "pointer",
                  margin: "0 2px",
                  color: index < row.original.rating ? "gold" : "lightgray",
                }}
              ></span>
            ))}
          </div>
        ),
      },
      {
        accessorKey: "remarks",
        header: "Remarks",
        // size: 150,
        Cell: ({ row }) => (
          <input
            type="text"
            className="form-control"
            value={row.original.remarks || ""}
            onChange={(e) => {
              const updatedData = activityAttendanceData.map((item) =>
                item.campusActivityApplicationId ===
                row.original.campusActivityApplicationId
                  ? { ...item, remarks: e.target.value }
                  : item
              );
              setActivityAttendanceData(updatedData);
            }}
          />
        ),
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
            const actions = getActionsForRole(roleId);
            const handlers = {
              handleGenerateCertificate: () =>
                handleGenerateCertificate(row.original.studentId, row.original.activityId),
              handlePrintCertificate: () => handlePrintCertificate(row.original.studentId),
            };
            return (
              <div className="d-flex gap-2">
                {actions.map((action) => {
                  if (action.visible && !action.visible(row.original)) {
                    return null;
                  }
                  return (
                    <button
                      key={action.name}
                      type="button"
                      className="btn p-0 border-0 custom-disabled-btn"
                      data-tooltip-id="global-tooltip"
                      data-tooltip-content={action.tooltip}
                      onClick={handlers[action.handler]}
                      disabled={action.disabled && action.disabled(row.original)}
                    >
                      <i className={action.icon}></i>
                    </button>
                  );
                })}
              </div>
            );
          },
      },
    ],
    [activityAttendanceData, roleId]
  );

  const exportToExcel = () => {
    const filteredData = activityAttendanceData.map((row, index) => ({
      "Sr No": index + 1,
      Pupil: row.studentName,
      Activity: row.activityName,
      Rating: row.rating,
      Remarks: row.remarks,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ActivityCertificate");
    XLSX.writeFile(workbook, "ActivityCertificate.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    const roleIdLoggedIn = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    const sidebarLogoUrl =
      roleIdLoggedIn == "2" ? orgLogo : roleIdLoggedIn == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Activity Certificate List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) => column.accessorKey !== "actions" && column.accessorKey !== "mrt-row-numbers"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = activityAttendanceData.map((row, index) => {
      return [
        index + 1,
        row.studentName,
        row.activityName,
        row.rating,
        row.remarks,
      ];
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("ActivityCertificateList.pdf");
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Activity Certificate</h2>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="mb-3">
            <div className="row g-3 new-form-style">
              <div className="col-md-2">
                <label className="form-label form-label-new">
                  Select Activity
                </label>
                <Controller
                  name="selectedActivity"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={activities}
                      isClearable
                      placeholder="Select an activity"
                      onChange={(option) => field.onChange(option)}
                      value={field.value}
                      className="react-select-style"
                      classNamePrefix="react-select-style-pref"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <CustomDatatableGrid
            columns={columns}
            data={activityAttendanceData}
            onExportExcel={exportToExcel}
            onExportPDF={exportToPDF}
            getRowId={(row) => row.rowKey}
            initialState={{
                columnPinning: { right: ["actions"] },
            }}
          />
        </div>
      </div>
    </>
  );
}

export default ActivityCertificate;
